import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import map from 'lodash/map';
import merge from 'lodash/merge';
import debounce from 'lodash/debounce';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import filter from 'lodash/filter';
import Select from 'react-select';
import Masonry from 'react-masonry-component';
import TextTransition from 'react-text-transition';
import ReactPlayer from 'react-player';
import ReactLoading from 'react-loading';
import ShareNavConfig from './components/share-icons.js';
import Img from 'react-image';
import BestPrice from '@sabre/shs-widgets-best-price';
import defaultLabels from './labels';
import './gallery.scss';
import VariantControls from './components/variant-controls';

class GalleryComponent extends Component {
    state = {
        images: this.props.images,
        categories: this.getCategories(this.props.images),
        category: this.props.category,
        activeImage: this.props.activeImage || -1,
        activeVariant: 0,
        width: window.innerWidth,
        isFullLightWindow: false,
        allowFullScreenZoom: this.props.allowFullScreenZoom,
    };

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.state.activeImage === -1 && prevState.activeImage > -1) {
            document.body.classList.remove('shs-widgets-gallery-modal-open');
        } else if (this.state.activeImage > -1 && prevState.activeImage === -1) {
            document.body.classList.add('shs-widgets-gallery-modal-open');
        }
        if (this.props.allowFullScreenZoom !== prevProps.allowFullScreenZoom) {
            this.setState({
                allowFullScreenZoom: this.props.allowFullScreenZoom,
            });
        }
        if (!isEqual(get(prevProps, 'images'), this.props.images)) {
            const categories = this.getCategories(this.props.images);
            const { category } = this.state;

            if (includes(categories, category)) {
                this.setState({
                    categories,
                    images: this.getFilteredImages(),
                });
            } else {
                this.setState(
                    {
                        category: 'all',
                    },
                    () =>
                        this.setState({
                            categories,
                            images: this.getFilteredImages(),
                        })
                );
            }
        }
    }

    componentDidMount() {
        this.debouncedResize = debounce(this.handleResize, 200);
        window.addEventListener('keyup', this.keyHandling);
        window.addEventListener('resize', this.debouncedResize);
    }

    componentWillUnmount() {
        window.removeEventListener('keyup', this.keyHandling);
        window.removeEventListener('resize', this.debouncedResize);
    }

    render() {
        const { categories, category, images } = this.state;
        const labels = merge({}, defaultLabels, this.props.labels);

        if (!this.props.api) {
            return <div>Missing API key.</div>;
        }

        return (
            <div className="shs-widgets-gallery">
                <nav className="shs-widgets-gallery--categories">
                    {this.isMobile() && (
                        <Select
                            className="category-select"
                            classNamePrefix="category-nav"
                            value={{
                                value: category,
                                label: category === 'all' ? labels.categoriesAll : category,
                            }}
                            options={[
                                {
                                    value: 'all',
                                    label: labels.categoriesAll,
                                },
                                ...map(categories, cat => ({ value: cat, label: cat })),
                            ]}
                            onChange={category => this.setCategory(category.value)}
                        />
                    )}
                    {!this.isMobile() && (
                        <ul>
                            <li>
                                <button
                                    className={`${category === 'all' ? 'active' : ''}`}
                                    onClick={() => this.setCategory('all')}
                                >
                                    {labels.categoriesAll}
                                </button>
                            </li>
                            {map(categories, cat => (
                                <li key={`category-nav-${cat}`}>
                                    <button
                                        className={`${cat === category ? 'active' : ''}`}
                                        onClick={() => this.setCategory(cat)}
                                    >
                                        {cat}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </nav>
                <Masonry
                    className="gallery-container"
                    options={{
                        fitWidth: true,
                        gutter: this.props.gutter,
                    }}
                >
                    {map(images, (img, index) => (
                        <div
                            onClick={() => this.setActiveImage(index)}
                            key={`block-image-${img.img}-${index}`}
                            className="shs-widgets-gallery--item"
                        >
                            <div className="shs-widgets-gallery--item__container">
                                <img
                                    style={{
                                        width: `${this.props.imageWidth}px`,
                                        marginBottom: `${this.props.gutter}px`,
                                    }}
                                    src={img.img}
                                    alt={img.alt}
                                />
                                {img.video && <div className="video-icon" />}
                            </div>
                        </div>
                    ))}
                </Masonry>
                {this.state.activeImage > -1 && this.renderDetail(labels)}
            </div>
        );
    }

    keyHandling = event => {
        if (this.state.activeImage > -1) {
            if (event.keyCode === 39) {
                this.goForward();
            }

            if (event.keyCode === 37) {
                this.goBack();
            }
        }
    };

    handleResize = event => {
        this.setState({
            width: window.innerWidth,
        });
    };

    renderDetail = labels => {
        const { socialShareHashTag, socialShareTitle, socialShareUrl, socialShareBody } = this.props;
        const { images, activeImage, activeVariant, isFullLightWindow, allowFullScreenZoom } = this.state;
        let activeObject = null;

        const imageObject = get(images, `[${activeImage}]`);

        if (!imageObject) {
            return null;
        }

        if (isEmpty(imageObject.variants)) {
            activeObject = imageObject;
        } else {
            activeObject = merge({}, imageObject, imageObject.variants[activeVariant]);
        }

        const { title, subTitle, description, imgFull, socialConfig, video } = activeObject;
        const socialConfigMerge = merge(
            {},
            {
                socialShareHashTag,
                socialShareTitle,
                socialShareUrl,
                socialShareBody,
            },
            socialConfig
        );

        return createPortal(
            <div className="shs-widgets-gallery--overlay">
                <div className="shs-widgets-gallery--modal">
                    <div
                        className={classNames('detail-image', {
                            'full-light-box': isFullLightWindow,
                            'scaled-light-box': !isFullLightWindow && allowFullScreenZoom,
                        })}
                        {...(allowFullScreenZoom && { onClick: this.toggleFullScreenLightBox })}
                    >
                        {video ? (
                            <ReactPlayer url={video} playing controls />
                        ) : (
                            <Img
                                src={imgFull}
                                loader={
                                    <div className="image-loading">
                                        <ReactLoading type="spin" color="#fff" height={40} width={40} />
                                    </div>
                                }
                            />
                        )}
                        {allowFullScreenZoom && (
                            <div
                                role="button"
                                className={classNames('light-box-zoom-icon', {
                                    'zoom-in': isFullLightWindow,
                                    'zoom-out': !isFullLightWindow,
                                })}
                                onClick={this.toggleFullScreenLightBox}
                            >
                                {isFullLightWindow && <i className="zoom-in" />}
                                {!isFullLightWindow && <i className="zoom-out" />}
                            </div>
                        )}
                    </div>
                    {isFullLightWindow && (
                        <ShareNavConfig
                            socialConfig={socialConfigMerge}
                            labels={labels}
                            isLightBox
                            title={title}
                            goBack={this.goBack}
                            goForward={this.goForward}
                        />
                    )}
                    {!isFullLightWindow && (
                        <div className="detail-info">
                            <button className="close" onClick={this.closeModal}>
                                ×
                            </button>
                            {subTitle && (
                                <div className="sub-title">
                                    <TextTransition text={subTitle} spring={{ stiffness: 120, damping: 17 }} />
                                </div>
                            )}
                            {title && (
                                <div className="title">
                                    <TextTransition text={title} spring={{ stiffness: 120, damping: 17 }} />
                                </div>
                            )}
                            <div className="description" dangerouslySetInnerHTML={{ __html: description }} />
                            {!isEmpty(imageObject.variants) && (
                                <VariantControls
                                    variants={imageObject.variants}
                                    onChange={index => this.setState({ activeVariant: index })}
                                    activeVariant={activeVariant}
                                />
                            )}
                            {this.renderControls(activeObject)}
                            {
                                <ShareNavConfig
                                    socialConfig={socialConfigMerge}
                                    labels={labels}
                                    goBack={this.goBack}
                                    goForward={this.goForward}
                                />
                            }
                        </div>
                    )}
                </div>
            </div>,
            document.body
        );
    };

    renderControls = data => {
        if (!data.controls) {
            return null;
        }

        return (
            <div className="controls">
                {map(data.controls, control => {
                    if (control.type === 'button') {
                        return (
                            <div className="control-item">
                                <button
                                    style={control.style}
                                    className={`control-button ${control.className}`}
                                    onClick={() => {
                                        if (this.props.isWebComponent) {
                                            return window[control.onClick] && window[control.onClick](data);
                                        }

                                        return control.onClick && control.onClick(data);
                                    }}
                                >
                                    {control.label}
                                </button>
                            </div>
                        );
                    }

                    if (control.type === 'price') {
                        return (
                            <div className="control-item">
                                <BestPrice
                                    apiWidget="widgets-gallery"
                                    api={this.props.api}
                                    onlyCheckRequested
                                    {...control.props}
                                />
                            </div>
                        );
                    }

                    return null;
                })}
            </div>
        );
    };

    goForward = () => {
        const { activeImage, images } = this.state;

        if (activeImage === images.length - 1) {
            return this.setState({
                activeImage: 0,
            });
        }

        return this.setState({
            activeImage: activeImage + 1,
        });
    };

    goBack = () => {
        const { activeImage, images } = this.state;

        if (activeImage === 0) {
            return this.setState({
                activeImage: images.length - 1,
            });
        }

        return this.setState({
            activeImage: activeImage - 1,
        });
    };

    setActiveImage = index => {
        this.setState({
            activeImage: index,
        });
    };

    toggleFullScreenLightBox = () => {
        this.setState({
            isFullLightWindow: !this.state.isFullLightWindow,
        });
    };

    closeModal = () => {
        this.setState({
            activeImage: -1,
        });
    };

    setCategory = code =>
        this.setState(
            {
                category: code,
            },
            () => this.setState({ images: this.getFilteredImages() })
        );

    getFilteredImages = () => {
        const { category } = this.state;

        if (category === 'all') {
            return this.props.images;
        }

        return filter(this.props.images, { category });
    };

    isMobile() {
        return this.state.width < 768;
    }

    getCategories(images) {
        return uniq(map(images, 'category'));
    }
}

GalleryComponent.propTypes = {
    activeImage: PropTypes.number,
    api: PropTypes.string.isRequired,
    category: PropTypes.string,
    gutter: PropTypes.number,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            img: PropTypes.string.isRequired,
            imgFull: PropTypes.string.isRequired,
            video: PropTypes.string,
            title: PropTypes.string,
            subTitle: PropTypes.string,
            description: PropTypes.string,
            category: PropTypes.string,
            socialConfig: PropTypes.shape({
                socialShareHashTag: PropTypes.array,
                socialShareTitle: PropTypes.string,
                socialShareUrl: PropTypes.string,
                socialShareBody: PropTypes.string,
            }),
            variants: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    imgFull: PropTypes.string.isRequired,
                    video: PropTypes.string,
                    title: PropTypes.string,
                    subTitle: PropTypes.string,
                    description: PropTypes.string,
                    socialConfig: PropTypes.shape({
                        socialShareHashTag: PropTypes.array,
                        socialShareTitle: PropTypes.string,
                        socialShareUrl: PropTypes.string,
                        socialShareBody: PropTypes.string,
                    }),
                    controls: PropTypes.arrayOf(
                        PropTypes.shape({
                            type: PropTypes.oneOf(['button', 'price']),
                            onClick: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
                            label: PropTypes.string,
                            className: PropTypes.string,
                            style: PropTypes.object,
                            props: PropTypes.object,
                        })
                    ),
                })
            ),
            controls: PropTypes.arrayOf(
                PropTypes.shape({
                    type: PropTypes.oneOf(['button', 'price']),
                    onClick: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
                    // if button it will be button label
                    label: PropTypes.string,
                    className: PropTypes.string,
                    style: PropTypes.object,
                    // if price it requires best price widget props i.e. {"type":"price","props":{"hotel":"10155","chain":"12011","room":"DLXK","labelSlogan":"Rates from","labelSlogan2":""}}
                    props: PropTypes.object,
                })
            ),
        })
    ).isRequired,
    imageWidth: PropTypes.number,
    allowFullScreenZoom: PropTypes.bool,
    isWebComponent: PropTypes.bool,
    labels: PropTypes.object,
    socialShareUrl: PropTypes.string,
    socialShareTitle: PropTypes.string,
    socialShareHashTag: PropTypes.array,
    socialShareBody: PropTypes.string,
};

GalleryComponent.defaultProps = {
    activeImage: -1,
    imageWidth: 280,
    gutter: 20,
    category: 'all',
    images: [],
    allowFullScreenZoom: false,
};

export default GalleryComponent;
